import { useForm, FormProvider, useFormContext } from "react-hook-form";

import { Dialog } from "ui/dialog";
import {
  CampaignSetupStep,
  CampaignSetupStepActions,
} from "components/modals/create-campaign-with-writer-modal/steps/campaign-setup-step";
import {
  DeliverablesSelectionStep,
  DeliverablesSelectionStepActions,
} from "components/modals/create-campaign-with-writer-modal/steps/deliverables-selection-step";
import {
  CampaignDetailsStep,
  CampaignDetailsStepActions,
} from "components/modals/create-campaign-with-writer-modal/steps/campaign-details-step";
import {
  EmailInstructionsStep,
  EmailInstructionsStepActions,
} from "components/modals/create-campaign-with-writer-modal/steps/email-instructions-step";

import {
  CampaignWithWriterContextProvider,
  useCampaignWithWriterContext,
} from "components/modals/create-campaign-with-writer-modal/context/campaign-with-writer-context";
import { useSelectedCampaignTemplate } from "components/modals/create-campaign-with-writer-modal/hooks/use-selected-campaign-template";
import {
  getDisplayStepInfo,
  shouldSkipCreativesStep,
} from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.helper";

import type {
  CreateCampaignWithWriterFormData,
  CreateCampaignWithWriterModalProps,
} from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.types";
import { CREATE_CAMPAIGN_WITH_WRITER_SCHEMA } from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.constants";

export const CreateCampaignWithWriterModal = ({
  setIsOpen = () => {},
  isOpen,
}: CreateCampaignWithWriterModalProps) => {
  const methods = useForm<CreateCampaignWithWriterFormData>({
    resolver: CREATE_CAMPAIGN_WITH_WRITER_SCHEMA,
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      programStrategy: "",
      transferStudentStatusFlag: false,
      campaignTemplateId: "",
      includeCreatives: false,
      deliverables: [],
      deliverableTemplates: {},
      campaignName: "",
      cdnSubdirectoryName: "",
      emailAdditionalInstructions: "",
    },
  });

  if (!isOpen) {
    return null;
  }

  return (
    <CampaignWithWriterContextProvider isOpen={isOpen} setIsOpen={setIsOpen}>
      <FormProvider {...methods}>
        <CreateCampaignWithWriterModalContent />
      </FormProvider>
    </CampaignWithWriterContextProvider>
  );
};

const CreateCampaignWithWriterModalContent = () => {
  const { step, setIsOpen } = useCampaignWithWriterContext();
  const { watch } = useFormContext<CreateCampaignWithWriterFormData>();

  const campaignTemplateId = watch("campaignTemplateId");
  const programStrategy = watch("programStrategy");
  const includeCreatives = watch("includeCreatives");

  const { deliverables, isLoadingDeliverables, templateSummary, templateValidationError } =
    useSelectedCampaignTemplate(campaignTemplateId, programStrategy);

  const hasCreatives = deliverables.length > 0;
  const skipCreativesStep = shouldSkipCreativesStep(hasCreatives, includeCreatives);
  const {
    isVisible: isStepIndicatorVisible,
    displayStep,
    totalSteps,
  } = getDisplayStepInfo(step, skipCreativesStep);

  const renderStep = () => {
    switch (step) {
      case 1:
        return <CampaignSetupStep hasCreatives={hasCreatives} />;
      case 2:
        return (
          <DeliverablesSelectionStep
            deliverables={deliverables}
            isLoading={isLoadingDeliverables}
          />
        );
      case 3:
        return (
          <CampaignDetailsStep
            templateSummary={templateSummary}
            templateValidationError={templateValidationError}
          />
        );
      case 4:
        return <EmailInstructionsStep />;
      default:
        return <CampaignSetupStep hasCreatives={hasCreatives} />;
    }
  };

  const renderActions = () => {
    switch (step) {
      case 1:
        return <CampaignSetupStepActions hasCreatives={hasCreatives} />;
      case 2:
        return <DeliverablesSelectionStepActions deliverables={deliverables} />;
      case 3:
        return (
          <CampaignDetailsStepActions
            templateValidationError={templateValidationError}
            hasCreatives={hasCreatives}
          />
        );
      case 4:
        return <EmailInstructionsStepActions />;
      default:
        return <CampaignSetupStepActions hasCreatives={hasCreatives} />;
    }
  };

  return (
    <Dialog
      open
      size={step === 2 ? "xlarge" : "medium"}
      onClose={() => setIsOpen(false)}
      headline="Create campaign from brief"
      step={isStepIndicatorVisible ? { current: displayStep, total: totalSteps } : undefined}
      actions={renderActions()}>
      <form className="flex flex-1 flex-col">{renderStep()}</form>
    </Dialog>
  );
};
